import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, Quote, Undo, Redo } from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const MenuBar = ({ editor }: { editor: any }) => {
  if (!editor) return null;

  const btn = (action: () => void, active: boolean, Icon: any) => (
    <button
      type="button"
      onClick={action}
      className={`p-1.5 rounded hover:bg-ui-surface-2 transition-colors ${
        active ? 'text-ui-accent bg-ui-surface-2' : 'text-ui-text-muted'
      }`}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div className="flex flex-wrap gap-1 p-1 border-b border-ui-border bg-ui-surface/50 rounded-t">
      {btn(() => editor.chain().focus().toggleBold().run(), editor.isActive('bold'), Bold)}
      {btn(() => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'), Italic)}
      <div className="w-px h-4 bg-ui-border self-center mx-1" />
      {btn(() => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'), List)}
      {btn(() => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'), ListOrdered)}
      {btn(() => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'), Quote)}
      <div className="w-px h-4 bg-ui-border self-center mx-1" />
      {btn(() => editor.chain().focus().undo().run(), false, Undo)}
      {btn(() => editor.chain().focus().redo().run(), false, Redo)}
    </div>
  );
};

export function RichTextEditor({ value, onChange }: Omit<Props, 'placeholder'>) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none max-w-none p-3 min-h-[120px] text-sm text-ui-text',
      },
    },
  });

  // Sincroniza cuando `value` cambia desde fuera (ej. el toggle ES/EN reutiliza
  // la misma instancia del editor con distinto contenido). Sin esto, TipTap solo
  // toma `content` como valor inicial y mostraría el idioma anterior.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  return (
    <div className="border border-ui-border rounded bg-ui-bg focus-within:border-ui-accent transition-colors">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
